import { useState } from "react";
import { CheckCircle, MessageSquare, FolderArchive, Wallet, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollReveal, useStaggeredReveal } from "@/hooks/useScrollReveal";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";

const features = [
  {
    icon: CheckCircle,
    title: "Vous savez exactement quoi faire",
    description: "Chaque étape est claire, expliquée et visible depuis votre espace."
  },
  {
    icon: MessageSquare,
    title: "Vous n'êtes plus seul face à l'administration",
    description: "Un interlocuteur dédié vous répond directement depuis votre espace."
  },
  {
    icon: FolderArchive,
    title: "Vos impôts sont suivis dans le temps",
    description: "Documents et déclarations restent centralisés pour les années suivantes."
  },
  {
    icon: Wallet,
    title: "Vous gardez le contrôle des coûts",
    description: "Pas d'abonnement. Vous payez uniquement si vous faites votre déclaration."
  }
];

const YOUTUBE_VIDEO_ID = "aL6OZOf1cF8";

const PlatformFeatures = () => {
  const { ref: headerRef, style: headerStyle } = useScrollReveal({ direction: 'up' });
  const { ref: featuresRef, isVisible, getItemStyle } = useStaggeredReveal(features.length, 100);
  const { trackCTA, trackEvent } = useGoogleAnalytics();
  
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  
  const thumbnailUrl = `https://img.youtube.com/vi/${YOUTUBE_VIDEO_ID}/hqdefault.jpg`;

  const handlePlayVideo = () => {
    setIsVideoLoaded(true);
    trackEvent('video_play', { video_id: YOUTUBE_VIDEO_ID, location: 'platform_demo' });
  };

  return (
    <section className="bg-white/50 py-8 md:py-12 lg:py-16" aria-labelledby="platform-title">
      <div className="container-custom">
        {/* Header */}
        <div 
          ref={headerRef}
          style={headerStyle}
          className="text-left mb-8 md:mb-12"
        >
          <h2 id="platform-title" className="text-[1.35rem] sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-heading font-extrabold text-green-primary mb-3 md:mb-4">
            Voilà à quoi votre déclaration d'impôts devrait ressembler.
          </h2>
          <p className="text-sm md:text-sm lg:text-base leading-relaxed text-muted-foreground max-w-2xl mb-3">
            Un espace clair, un interlocuteur fiscal qui vous répond, et vos impôts suivis au même endroit, année après année.
          </p>
          <p className="text-xs md:text-sm leading-relaxed text-muted-foreground/80 max-w-2xl">
            Notre plateforme a été conçue pour les résidents suisses, les titulaires de permis B ou G imposés à la source, et les frontaliers. Que vous ayez besoin d'une simple déclaration ou d'une optimisation fiscale complète, Easy Taxes centralise vos documents et vous connecte directement avec un conseiller dédié.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-10 lg:gap-14 items-center mb-8 md:mb-12">
          {/* Video */}
          <div 
            className="order-1 md:order-1 relative"
            id="platform-section"
          >
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-soft bg-black">
              {!isVideoLoaded ? (
                <button 
                  onClick={handlePlayVideo}
                  className="w-full h-full relative group cursor-pointer"
                  aria-label="Lancer la vidéo de démonstration Easy Taxes"
                >
                  <img 
                    src={thumbnailUrl}
                    alt="Aperçu de la démonstration Easy Taxes - Plateforme de déclaration d'impôts en Suisse"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Play className="w-7 h-7 md:w-8 md:h-8 text-white ml-1" fill="currentColor" />
                    </div>
                  </div>
                  <span className="absolute bottom-4 left-4 text-sm font-medium text-white bg-black/60 px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Voir la démonstration
                  </span>
                </button>
              ) : (
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
                  title="Démonstration de la plateforme Easy Taxes - Déclaration d'impôts en Suisse"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
            <p id="video-description" className="sr-only">
              Cette vidéo présente la plateforme Easy Taxes : un espace clair pour gérer votre déclaration d'impôts en Suisse, 
              une messagerie directe avec un conseiller fiscal dédié, et un suivi centralisé de tous vos documents fiscaux année après année.
            </p>
          </div>

          {/* Features */}
          <div 
            ref={featuresRef}
            className="order-2 md:order-2"
          >
            <ul className="space-y-4 md:space-y-5" aria-label="Avantages de la plateforme Easy Taxes">
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <li 
                    key={index}
                    style={getItemStyle(index)}
                    className="flex items-start gap-3 md:gap-4 bg-white p-3 md:p-4 rounded-xl shadow-sm transition-shadow duration-300 hover:shadow-md"
                  >
                    <div className="flex-shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-full bg-green-primary/10 flex items-center justify-center">
                      <IconComponent className="w-4 h-4 md:w-5 md:h-5 text-green-primary" aria-hidden="true" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-sm md:text-base font-heading font-semibold text-foreground mb-0.5 md:mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            asChild
            size="lg"
            className="w-full sm:w-auto min-h-[56px] px-6 sm:px-10 text-base sm:text-lg font-semibold bg-green-primary hover:bg-green-primary/90 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
            aria-label="Créer mon espace sécurisé Easy Taxes"
            onClick={() => trackCTA('cta_click_signup', 'platform_cta', 'platform_features')}
          >
            <a
              href="https://app.easy-taxes.ch/auth"
              target="_blank"
              rel="noopener noreferrer"
            >
              Créer mon espace sécurisé
            </a>
          </Button>
          <p className="mt-3 text-xs md:text-sm text-muted-foreground">
            Sans engagement • Accès immédiat • Premiers conseils gratuits
          </p>
          <p className="mt-1">
            <a 
              href="/tarifs" 
              className="text-xs md:text-sm font-medium text-gold-text hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 rounded-sm"
              aria-label="Découvrir nos tarifs de déclaration d'impôts"
            >
              Découvrir nos tarifs →
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default PlatformFeatures;
